import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import { User } from "@coworker/apprestructured/src/shared/types/user";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import { FixaSegmentedControl } from "@coworker/apprestructured/src/shared/wrappers/FixaSegmentedControl/FixaSegmentedControl";
import ErrorAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import {
  clean,
  makeMatcher,
} from "@coworker/components/src/helpers/string.helpers";
import { useTranslation } from "@coworker/locales";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import { useCallback, useMemo, useState } from "react";
import ModalColumn from "../ModalColumn/ModalColumn";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import styles from "./AssignTo.module.css";
import { TeamsList } from "./TeamsList/TeamsList";
import { UsersList } from "./UsersList/UsersList";

const useMatcher = ({
  searchQuery,
  teams,
  users,
}: {
  searchQuery: string;
  teams: StoreTeam[];
  users: User[];
}) => {
  const matchAll = useMemo(() => makeMatcher(searchQuery, true), [searchQuery]);

  const matcher = useCallback(
    (query: string) => !matchAll || matchAll.test(clean(query)),
    [matchAll]
  );

  const matchingTeams = useMemo(() => {
    const filtered = teams.filter((team) => matcher(team.name));

    // NOTE:
    //   sort() mutates the array,
    //   but it is ok because filter() creates a new array
    //   and does not mutate the original one.
    return filtered.sort((teamA, teamB) =>
      teamA.name.localeCompare(teamB.name)
    );
  }, [teams, matcher]);

  const matchingUsers = useMemo(
    () => users.filter((user) => matcher(user.fullName)),
    [users, matcher]
  );

  return { matchingTeams, matchingUsers };
};

const noop = () => {};

export type OnConfirmClickResult = {
  teamId: string | null;
  userId: string | null;
};

type AssignToModalProps = {
  visible: boolean;
  onClose: () => void;
  searchQuery: string;
  isTeamsLoading: boolean;
  isUsersLoading: boolean;
  teams: StoreTeam[];
  users: User[];
  myTeamId: string;
  onSearchQueryChange: (query: string) => void;
  mostRecentAssignedUser?: User | null;
  mostRecentAssignedTeam?: StoreTeam | null;
  onUserIdChange?: (userId: string) => void;
  onTeamIdChange?: (teamId: string) => void;
  onConfirmClick: (result: OnConfirmClickResult) => void;
  initialAssignedUserId?: string | null;
  initialAssignedTeamId?: string | null;
};

export const AssignToModal = ({
  visible,
  onClose,
  searchQuery,
  isTeamsLoading,
  isUsersLoading,
  teams,
  users,
  myTeamId,
  onSearchQueryChange,
  mostRecentAssignedUser = null,
  mostRecentAssignedTeam = null,
  onTeamIdChange = noop,
  onUserIdChange = noop,
  onConfirmClick,
  initialAssignedUserId = null,
  initialAssignedTeamId = null,
}: AssignToModalProps) => {
  const myUserId = useUID();
  const [selectedSuggestion, setSelectedSuggestion] = useState<
    "team" | "user" | "recentTeam" | "recentUser" | null
  >(() =>
    initialAssignedUserId === myUserId
      ? "user"
      : !initialAssignedUserId && initialAssignedTeamId === myTeamId
        ? "team"
        : null
  );
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    initialAssignedUserId
  );
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(
    initialAssignedTeamId
  );
  const [noSelectedItem, setNoSelectedItem] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState(
    initialAssignedUserId ? 1 : 0
  );
  const isTeamsSelected = selectedIndex === 0;
  const isCoworkersSelected = selectedIndex === 1;

  const { matchingTeams, matchingUsers } = useMatcher({
    searchQuery,
    teams,
    users,
  });
  const currentUser = users.find((user) => user.uid === myUserId);
  const currentTeam = teams.find((team) => team.id === myTeamId);
  const showRecentAssignee = mostRecentAssignedUser
    ? mostRecentAssignedUser.uid !== currentUser?.uid
    : !!mostRecentAssignedTeam && mostRecentAssignedTeam.id !== currentTeam?.id;

  const handleUserIdChange = useCallback(
    (userId: string) => {
      setSelectedSuggestion(null);
      setSelectedUserId(userId);
      setNoSelectedItem(false);
      onUserIdChange(userId);
    },
    [onUserIdChange]
  );

  const handleTeamIdChange = useCallback(
    (teamId: string) => {
      setSelectedSuggestion(null);
      setSelectedTeamId(teamId);
      setNoSelectedItem(false);
      onTeamIdChange(teamId);
    },
    [onTeamIdChange]
  );

  const handleSuggestedUserClick = useCallback(
    (userId: string, recent = false) => {
      setSelectedSuggestion(recent ? "recentUser" : "user");
      setSelectedTeamId(null);
      setSelectedUserId(userId);
      setNoSelectedItem(false);
      onUserIdChange(userId);
    },
    [onUserIdChange]
  );

  const handleSuggestedTeamClick = useCallback(
    (teamId: string, recent = false) => {
      setSelectedSuggestion(recent ? "recentTeam" : "team");
      setSelectedUserId(null);
      setSelectedTeamId(teamId);
      setNoSelectedItem(false);
      onTeamIdChange(teamId);
    },
    [onTeamIdChange]
  );

  const handleSearchForCoworkersClick = useCallback(() => {
    setSelectedSuggestion(null);
    setSelectedIndex(1);
  }, []);

  const handleSearchForTeamsClick = useCallback(() => {
    setSelectedSuggestion(null);
    setSelectedIndex(0);
  }, []);

  const handleClearSearchClick = useCallback(() => {
    onSearchQueryChange("");
  }, [onSearchQueryChange]);

  const handleSegmentedControlClick = useCallback((_: unknown, i: number) => {
    setSelectedSuggestion(null);
    setSelectedIndex(i);
    setNoSelectedItem(false);
  }, []);

  const handleConfirmClick = useCallback(() => {
    const confirmingTeam = selectedSuggestion
      ? selectedSuggestion === "team" || selectedSuggestion === "recentTeam"
      : isTeamsSelected;
    const confirmingCoworker = !confirmingTeam;

    if (confirmingTeam && !selectedTeamId) {
      setNoSelectedItem(true);
      return;
    }
    if (confirmingCoworker && !selectedUserId) {
      setNoSelectedItem(true);
      return;
    }

    const selectedUser = confirmingCoworker
      ? (matchingUsers.find((user) => user.uid === selectedUserId) ??
        (selectedUserId === myUserId ? currentUser : undefined) ??
        (selectedUserId === mostRecentAssignedUser?.uid
          ? mostRecentAssignedUser
          : undefined))
      : null;

    if (confirmingCoworker && !selectedUser) {
      setNoSelectedItem(true);
      return;
    }

    onConfirmClick({
      teamId: confirmingCoworker ? selectedUser!.teamId : selectedTeamId!,
      userId: confirmingTeam ? null : selectedUserId,
    });
  }, [
    isTeamsSelected,
    selectedTeamId,
    selectedUserId,
    selectedSuggestion,
    onConfirmClick,
    matchingUsers,
    myUserId,
    currentUser,
    mostRecentAssignedUser,
  ]);

  const { t } = useTranslation();
  const labels = useMemo<{ label: string; type: "button" }[]>(
    () => [
      { label: t("teamsString"), type: "button" },
      { label: t("coworkersString"), type: "button" },
    ],
    [t]
  );

  return (
    <FixaModal visible={visible} handleCloseBtn={onClose}>
      <FixaSheets
        labelledById="task-management-assign-to-header"
        header={
          <FixaModalHeader
            id="task-management-assign-to-header"
            title={t("assignString")}
            closeBtnClick={onClose}
          />
        }
        footer={
          <StyledModalFooter>
            <div className={styles["footerWrapper"]}>
              {noSelectedItem && (
                <ErrorAddOn>{t("pleaseFillInString")}</ErrorAddOn>
              )}
              <FixaButton
                text={t("confirmString")}
                onClick={handleConfirmClick}
                type="primary"
                data-testid="assign-to-confirm-button"
              />
            </div>
          </StyledModalFooter>
        }
        fullSize
      >
        <FixaModalBody>
          <ModalColumn gap="medium">
            {(showRecentAssignee || currentTeam || currentUser) && (
              <>
                <FixaListView id="assign-to-suggestions-heading">
                  <FixaListViewItem
                    title={t("suggestionsString", {
                      defaultValue: "Suggestions",
                    })}
                    disabled
                    showDivider
                  />
                </FixaListView>
                {showRecentAssignee &&
                  (mostRecentAssignedUser ? (
                    <FixaListView id="assign-to-suggested-recent-assignee">
                      <FixaListViewItem
                        emphasised
                        title={mostRecentAssignedUser.fullName}
                        description={
                          teams.find(
                            (team) => team.id === mostRecentAssignedUser.teamId
                          )?.name ?? ""
                        }
                        control="radiobutton"
                        controlProps={{
                          checked: selectedSuggestion === "recentUser",
                          value: mostRecentAssignedUser.uid,
                        }}
                        onChange={() =>
                          handleSuggestedUserClick(
                            mostRecentAssignedUser.uid,
                            true
                          )
                        }
                      />
                    </FixaListView>
                  ) : mostRecentAssignedTeam ? (
                    <FixaListView id="assign-to-suggested-recent-assignee">
                      <FixaListViewItem
                        emphasised
                        title={mostRecentAssignedTeam.name}
                        control="radiobutton"
                        controlProps={{
                          checked: selectedSuggestion === "recentTeam",
                          value: mostRecentAssignedTeam.id,
                        }}
                        onChange={() =>
                          handleSuggestedTeamClick(
                            mostRecentAssignedTeam.id,
                            true
                          )
                        }
                      />
                    </FixaListView>
                  ) : null)}
                {currentUser && (
                  <FixaListView id="assign-to-suggested-user">
                    <FixaListViewItem
                      emphasised
                      title={currentUser.fullName}
                      description={
                        teams.find((team) => team.id === currentUser.teamId)
                          ?.name ?? ""
                      }
                      control="radiobutton"
                      controlProps={{
                        checked: selectedSuggestion === "user",
                        value: currentUser.uid,
                      }}
                      onChange={() => handleSuggestedUserClick(currentUser.uid)}
                    />
                  </FixaListView>
                )}
                {currentTeam && (
                  <FixaListView id="assign-to-suggested-team">
                    <FixaListViewItem
                      emphasised
                      title={currentTeam.name}
                      description={t("myCurrentTeamString")}
                      control="radiobutton"
                      controlProps={{
                        checked: selectedSuggestion === "team",
                        value: currentTeam.id,
                      }}
                      onChange={() => handleSuggestedTeamClick(currentTeam.id)}
                    />
                  </FixaListView>
                )}
              </>
            )}
            <FixaSegmentedControl
              items={labels}
              activeIndex={selectedIndex}
              size="medium"
              onClick={handleSegmentedControlClick}
            />
            <FixaSearch
              id="assign-to-search"
              value={searchQuery}
              placeholder={t(
                `${isTeamsSelected ? "searchTeamString" : "searchCoworkerString"}`
              )}
              onChange={(e) => {
                onSearchQueryChange(e.target.value);
              }}
              onClear={handleClearSearchClick}
            />

            {isTeamsSelected && (
              <TeamsList
                matchingTeams={matchingTeams}
                isLoading={isTeamsLoading}
                myTeamId={myTeamId}
                searchQuery={searchQuery}
                assignedTeamId={selectedSuggestion ? null : selectedTeamId}
                onTeamIdChange={handleTeamIdChange}
                onSearchForCoworkersClick={handleSearchForCoworkersClick}
                onClearSearchClick={handleClearSearchClick}
              />
            )}

            {isCoworkersSelected && (
              <UsersList
                matchingUsers={matchingUsers}
                isLoading={isUsersLoading}
                teams={teams}
                searchQuery={searchQuery}
                assignedUserId={selectedSuggestion ? null : selectedUserId}
                onUserIdChange={handleUserIdChange}
                onSearchForTeamsClick={handleSearchForTeamsClick}
                onClearSearchClick={handleClearSearchClick}
              />
            )}
          </ModalColumn>
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
